import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useAuth } from './hooks/useAuth';
import { isValidEmail } from '../../utils/email';
import { normalizeEmail } from '../../utils/normalizeInput';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { PasswordInput } from '../shared/PasswordInput';
import './LoginPage.css';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [attemptedLogin, setAttemptedLogin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { error: authError, user } = useAuth();

  useEffect(() => {
    if (location.state?.message) setInfo(location.state.message);
  }, [location.state]);

  useEffect(() => {
    if (authError) {
      setError(authError);
      setLoading(false);
    }
  }, [authError]);

  useEffect(() => {
    // After a login attempt, if user is set and no error, navigate to dashboard
    if (attemptedLogin && user && !authError) {
      navigate('/dashboard');
    }
  }, [user, authError, attemptedLogin, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    setAttemptedLogin(true);

    try {
      await authService.login(normalizeEmail(email), password);
      // Wait for onAuthStateChange to check status and navigate
    } catch (err) {
      setError(mapSupabaseError(err));
      setLoading(false);
      setAttemptedLogin(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setInfo('');
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) {
      setError('Enter your email above, then click Forgot Password.');
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      setError('Enter a valid email address.');
      return;
    }
    try {
      await authService.resetPassword(normalizedEmail);
      setInfo('If that email is registered, a password reset link has been sent.');
    } catch (err) {
      setError(mapSupabaseError(err));
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>WeGolden</h1>
        <p className="subtitle">Employee KPI Tracker</p>

        <form onSubmit={handleLogin} noValidate>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={loading}
              autoComplete="email"
              maxLength={254}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          {error && <div className="error-message" role="alert">{error}</div>}
          {info && <div className="info-message">{info}</div>}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Logging in...' : 'Log In'}
          </button>
        </form>

        <div className="form-footer">
          <button
            type="button"
            className="link-button"
            onClick={handleForgotPassword}
            disabled={loading}
          >
            Forgot Password?
          </button>
          <p>
            New user? <a href="/register">Register here</a>
          </p>
        </div>
      </div>
    </div>
  );
}
